import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { treatmentAnalyticsService } from '../../../services/treatmentAnalyticsService';
import { productService } from '../../../services/productService';

import Icon from '../../../components/AppIcon';
import { TrendingUp, TrendingDown, Award, Target, ExternalLink, ShoppingCart, Star } from 'lucide-react';

const TreatmentRankingTable = ({ filters }) => {
  const { user } = useAuth();
  const [rankings, setRankings] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('effectiveness');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadTreatmentRankings();
  }, [filters, sortBy, sortOrder]);

  const loadTreatmentRankings = async () => {
    try {
      setLoading(true);
      setError('');

      // Load both treatment analytics and products data
      const [analyticsData, productsData] = await Promise.all([
        treatmentAnalyticsService?.getTreatmentRankings({
          ...filters,
          sortBy,
          sortOrder
        }),
        productService?.getProducts()
      ]);

      // Merge analytics data with product database
      const enrichedRankings = analyticsData?.map(treatment => {
        const product = productsData?.find(p => p?.id === treatment?.product_id);
        return {
          ...treatment,
          product_info: product || null
        };
      });

      // Add database products that might not have analytics yet
      const productsWithoutAnalytics = // Limit to prevent overwhelming the table
      productsData?.filter(product => !analyticsData?.some(t => t?.product_id === product?.id))?.slice(0, 5)?.map(product => ({
          id: `db_${product?.id}`,
          product_id: product?.id,
          product_name: product?.name,
          effectiveness_score: null,
          usage_count: 0,
          avg_cost_per_acre: null,
          roi_percentage: null,
          user_rating: product?.rating || 0,
          trend: 'new',
          product_info: product,
          from_database: true
        }));

      setRankings([...enrichedRankings, ...productsWithoutAnalytics]);
      setProducts(productsData);
    } catch (err) {
      console.error('Error loading treatment rankings:', err);
      setError('Failed to load treatment rankings: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getTrendIcon = (trend, effectivenessScore) => {
    if (trend === 'up' || effectivenessScore > 85) return <TrendingUp className="w-4 h-4 text-success" />;
    if (trend === 'down' || (effectivenessScore && effectivenessScore < 70)) return <TrendingDown className="w-4 h-4 text-destructive" />;
    if (trend === 'new') return <Star className="w-4 h-4 text-primary" />;
    return <div className="w-4 h-4" />;
  };

  const getEffectivenessColor = (score) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 90) return 'text-success font-semibold';
    if (score >= 75) return 'text-primary font-medium';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const renderActionWidgets = (treatment) => {
    const { product_info } = treatment;
    if (!product_info) return null;

    return (
      <div className="flex items-center space-x-2">
        {/* View Product Details */}
        <button
          onClick={() => window.open(`/product-browser?id=${product_info?.id}`, '_blank')}
          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
          title="View Product Details"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
        {/* Purchase Link */}
        {product_info?.purchase_link && (
          <button
            onClick={() => window.open(product_info?.purchase_link, '_blank')}
            className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
            title="Purchase Product"
          >
            <ShoppingCart className="w-4 h-4" />
          </button>
        )}
        {/* Alternative Purchase */}
        {product_info?.alternate_purchase_link && (
          <button
            onClick={() => window.open(product_info?.alternate_purchase_link, '_blank')}
            className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
            title="Alternative Supplier"
          >
            <Icon name="Store" size={16} />
          </button>
        )}
        {/* Product Label */}
        {product_info?.product_label_link && (
          <button
            onClick={() => window.open(product_info?.product_label_link, '_blank')}
            className="p-1.5 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded transition-colors"
            title="View Product Label"
          >
            <Icon name="FileText" size={16} />
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3]?.map((i) => (
              <div key={i} className="h-12 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Award className="w-5 h-5 text-yellow-500" />
              <span>Treatment Performance Rankings</span>
            </h2>
            <p className="text-sm text-muted-foreground">
              Performance analysis with integrated product database and action widgets
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-muted-foreground">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => handleSort(e?.target?.value)}
              className="text-xs border border-border rounded px-2 py-1"
            >
              <option value="effectiveness">Effectiveness</option>
              <option value="usage_count">Usage Count</option>
              <option value="roi_percentage">ROI</option>
              <option value="user_rating">User Rating</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-border">
              <tr className="text-left">
                <th className="pb-3 text-sm font-medium text-muted-foreground">Rank</th>
                <th className="pb-3 text-sm font-medium text-muted-foreground">Product</th>
                <th 
                  className="pb-3 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={() => handleSort('effectiveness')}
                >
                  Effectiveness {sortBy === 'effectiveness' && (
                    <Icon name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={12} className="inline ml-1" />
                  )}
                </th>
                <th 
                  className="pb-3 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={() => handleSort('usage_count')}
                >
                  Usage Count {sortBy === 'usage_count' && (
                    <Icon name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={12} className="inline ml-1" />
                  )}
                </th>
                <th 
                  className="pb-3 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={() => handleSort('roi_percentage')}
                >
                  ROI {sortBy === 'roi_percentage' && (
                    <Icon name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={12} className="inline ml-1" />
                  )}
                </th>
                <th className="pb-3 text-sm font-medium text-muted-foreground">Trend</th>
                <th className="pb-3 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rankings?.map((treatment, index) => (
                <tr key={treatment?.id} className="hover:bg-muted/20">
                  <td className="py-4">
                    <div className="flex items-center space-x-2">
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                        ${index === 0 ? 'bg-yellow-100 text-yellow-800' :
                          index === 1 ? 'bg-gray-100 text-gray-700' :
                          index === 2 ? 'bg-orange-100 text-orange-700': 'bg-muted text-muted-foreground'}
                      `}>
                        {index + 1}
                      </div>
                      {index < 3 && (
                        <Award className={`w-4 h-4 ${
                          index === 0 ? 'text-yellow-500' :
                          index === 1 ? 'text-gray-400': 'text-orange-500'
                        }`} />
                      )}
                    </div>
                  </td>
                  
                  <td className="py-4">
                    <div>
                      <div className="font-medium text-foreground">{treatment?.product_name}</div>
                      {treatment?.product_info && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <div>{treatment?.product_info?.active_ingredients}</div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                              {treatment?.product_info?.category}
                            </span>
                            {treatment?.product_info?.price && (
                              <span className="text-xs text-muted-foreground">
                                {treatment?.product_info?.price}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {treatment?.from_database && (
                        <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded mt-1">
                          From Product Database
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="py-4">
                    <div className={`font-medium ${getEffectivenessColor(treatment?.effectiveness_score)}`}>
                      {treatment?.effectiveness_score ? `${treatment?.effectiveness_score?.toFixed(1)}%` : 'No Data'}
                    </div>
                    {treatment?.effectiveness_score && (
                      <div className="w-20 bg-muted rounded-full h-2 mt-1">
                        <div 
                          className={`h-2 rounded-full ${
                            treatment?.effectiveness_score >= 90 ? 'bg-success' :
                            treatment?.effectiveness_score >= 75 ? 'bg-primary' :
                            treatment?.effectiveness_score >= 60 ? 'bg-warning': 'bg-destructive'
                          }`}
                          style={{ width: `${treatment?.effectiveness_score}%` }}
                        />
                      </div>
                    )}
                  </td>
                  
                  <td className="py-4">
                    <span className="font-medium text-foreground">
                      {treatment?.usage_count || 0}
                    </span>
                    <div className="text-xs text-muted-foreground">applications</div>
                  </td>
                  
                  <td className="py-4">
                    <span className="font-medium text-foreground">
                      {treatment?.roi_percentage ? `${treatment?.roi_percentage?.toFixed(0)}%` : 'N/A'}
                    </span>
                    {treatment?.avg_cost_per_acre && (
                      <div className="text-xs text-muted-foreground">
                        {formatCurrency(treatment?.avg_cost_per_acre)}/acre
                      </div>
                    )}
                  </td>
                  
                  <td className="py-4">
                    <div className="flex items-center space-x-1">
                      {getTrendIcon(treatment?.trend, treatment?.effectiveness_score)}
                      <span className="text-xs text-muted-foreground capitalize">
                        {treatment?.trend || 'new'}
                      </span>
                    </div>
                  </td>
                  
                  <td className="py-4">
                    {renderActionWidgets(treatment)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rankings?.length === 0 && !loading && (
          <div className="text-center py-8">
            <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Rankings Available</h3>
            <p className="text-muted-foreground">
              Start tracking treatments to see performance rankings.
            </p>
          </div>
        )}

        {/* Database Integration Notice */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Database" size={16} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Enhanced with Product Database</h4>
              <p className="text-xs text-blue-800 mt-1">
                Rankings now include products from our comprehensive IPM database with purchase links, 
                labels, and detailed product information. New products appear as "No Data" until tracked.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentRankingTable;